import React, { useEffect, useState } from 'react';
import Navbar from '../../Components/Common/Navbar';
import Footer from '../../Components/Common/Footer';
import { Container } from '../../Components/ui/Container';
import { useIsMobile } from '../../Hooks/useIsMobile';
import { getImagePath, checkLPData } from '../../Utils/Utilities';
import { DemoBookingFlow } from './BookDemo';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../Components/ui/Button';

const demoFeaturesWithIcons = [
    { name: 'CRM', icon: 'fa fa-users' },
    { name: 'Quotes', icon: 'fas fa-file-alt' },
    { name: 'Orders', icon: 'fas fa-box' },
    { name: 'Billing', icon: 'fas fa-money-bill-wave' },
    { name: 'E-Way Bill', icon: 'fas fa-truck' },
    { name: 'E-Invoice', icon: 'fas fa-file-invoice' },
    { name: 'Accounts', icon: 'fas fa-inr' },
    // { name: 'GSTR1 & 3B', icon: 'fas fa-landmark' },
    { name: 'Recovery', icon: 'fas fa-hand-holding-usd' },
    { name: 'Inventory', icon: 'fas fa-warehouse' },
    // { name: 'Batches', icon: 'fas fa-layer-group' },
    // { name: 'BOM', icon: 'fas fa-clipboard-list' },
    // { name: 'Purchase Orders', icon: 'fa fa-shopping-cart' },
    // { name: 'Suppliers Invoices', icon: 'fas fa-user-tie' },
    // { name: 'MRP', icon: 'fas fa-calculator' },
    { name: 'Projects', icon: 'fas fa-project-diagram' },
    { name: 'Manufacturing', icon: 'fas fa-industry' },
    // { name: 'Tasks', icon: 'fas fa-tasks' },
    // { name: 'Support Ticketing', icon: 'fas fa-headset' },
    // { name: 'Contracts', icon: 'fas fa-file-contract' },
];

const getDemoQA = (brand) => {
    const name = brand === 'bz' ? 'Biziverse' : 'QikERP';
    return [
        { question: "Losing track of leads?", answer: `${name}'s CRM captures every inquiry and auto-reminds your team to follow up — no lead slips through.` },
        { question: "Billing still manual?", answer: `Generate GST invoices, E-Way Bills, and E-Invoices in seconds with ${name}.` },
        { question: "Inventory going out of stock?", answer: "Real-time stock tracking with batch & expiry management keeps you always prepared." },
        { question: "Chasing payments every month?", answer: `${name}'s Recovery module automates payment reminders so you get paid faster.` },
        { question: "No visibility on production?", answer: `Track BOM, MRP, and Manufacturing progress live — all in one place with ${name}.` },
    ];
};

const DemoQASection = ({ onBuyNow, brand }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showAnswer, setShowAnswer] = useState(false);
    const isMobile = useIsMobile();
    const demoQA = getDemoQA(brand);
    useEffect(() => {
        setShowAnswer(false);
        const t = setTimeout(() => setShowAnswer(true), 900);
        return () => clearTimeout(t);
    }, [currentIndex]);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex(prev => (prev + 1) % demoQA.length);
        }, 5500);
        return () => clearInterval(interval);
    }, []);

    const handlePrev = () => setCurrentIndex(prev => (prev - 1 + demoQA.length) % demoQA.length);
    const handleNext = () => setCurrentIndex(prev => (prev + 1) % demoQA.length);

    return (
        <div className="w-full bg-background-light  border-border py-16 lg:py-20 px-8 flex flex-col items-center">
            <div className="w-full max-w-[1600px] mx-auto">
                <div className="flex items-center justify-center gap-6">
                    <button onClick={handlePrev} className="text-primary hover:text-secondary transition-colors p-2">
                        <i className="fas fa-chevron-left text-xl"></i>
                    </button>
                    <div className="flex-1 max-w-3xl text-center min-h-[120px] flex flex-col items-center justify-center">
                        <h3 className="text-[30px] font-bold text-text-primary mb-4 leading-tight">
                            {demoQA[currentIndex].question}
                        </h3>
                        <p
                            className={`text-[18px] text-text-secondary font-medium transition-opacity duration-700 ease-out ${showAnswer ? 'opacity-100' : 'opacity-0'}`}
                        >
                            {demoQA[currentIndex].answer}
                        </p>
                    </div>
                    <button onClick={handleNext} className="text-primary hover:text-secondary transition-colors p-2">
                        <i className="fas fa-chevron-right text-xl"></i>
                    </button>
                </div>
                {/* <div className="flex justify-center gap-2 mt-8">
                    {demoQA.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setCurrentIndex(i)}
                            className={`w-2.5 h-2.5 rounded-full transition-all ${i === currentIndex ? 'bg-primary scale-125' : 'bg-border'}`}
                        />
                    ))}
                </div> */}
                <div className="flex justify-center mt-8">
                    <Button
                        type="button"
                        variant="primary"
                        onClick={onBuyNow}
                        size={!isMobile ? "lg" : "default"}
                    >
                        Buy Now
                    </Button>
                </div>
            </div>
        </div>
    );
};

const BookDemo = ({ brand }) => {
    const isMobile = useIsMobile();
    const [showThankYou, setShowThankYou] = useState(false);
    const [searchParams] = useSearchParams();
    useEffect(() => {
        if (!checkLPData('demo', searchParams)) return;
    }, []);

    const getQueryParam = (param) => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(param);
    };

    const handleBuyNow = () => {
        const i = getQueryParam('i') || '';
        const o = getQueryParam('o') || '';
        window.location.href = `/QikERP/Guide?i=${i}&o=${o}&z=offer`;
    };

    return (
        <div className="min-h-screen bg-white md:pt-16 flex flex-col">
            <Navbar logo={brand === 'bz' ? getImagePath("/Images/Biziverse.png") : getImagePath("/Images/QikERP.png")} onBuyNowClick={handleBuyNow} showBuyNow={true} />

            <main className="flex-grow flex">
                <section className="bg-white pt-8 md:pt-16 lg:pt-20 w-full">
                    {!isMobile ? (
                        <div className="w-full max-w-[1600px] mx-auto pl-8">
                            <div className="grid grid-cols-2 gap-8 min-h-[80vh] items-start">
                                {/* Left Column - Brand Panel */}
                                <div className="flex flex-col justify-start top-20 pt-0 self-start">
                                    <div className="flex flex-col items-start mb-8">
                                        {brand === 'bz' ? (
                                            <img
                                                src={getImagePath("/Images/Biziverse.png")}
                                                alt="Biziverse"
                                                className="h-[95px] w-auto object-contain"
                                            />
                                        ) : (
                                            <div className="relative inline-flex flex-col items-start">
                                                <img
                                                    src={getImagePath("/Images/QikERP.png")}
                                                    alt="QikERP"
                                                    className="h-[95px] w-auto object-contain"
                                                />
                                                <div className="flex flex-col items-center absolute" style={{ bottom: '-38px', right: '8px' }}>
                                                    <span className="text text-primary font-bold leading-none mb-0.5">by</span>
                                                    <img
                                                        src={getImagePath("/Images/Biziverse.png")}
                                                        alt="Biziverse"
                                                        className="h-[32px] w-auto object-contain"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        <div className="h-[38px]" />
                                    </div>
                                    <h1 className="text-[40px] font-bold text-left !leading-[1.25] mb-8">
                                        <span className="text-primary">Easy</span> & <span className="text-primary">Powerful</span> Tools to
                                        <br />
                                        <span className="text-primary">Accelerate Business</span>
                                    </h1>
                                    <div className="flex flex-wrap gap-3 mb-10">
                                        {demoFeaturesWithIcons.map((feature, idx) => (
                                            <div key={idx} className="flex items-center gap-2 border border-border rounded px-3 py-2 bg-background-light">
                                                <i className={`${feature.icon} flex-shrink-0 text-primary`} style={{ fontSize: '16px' }}></i>
                                                <span className="text-sm text-text-primary font-bold">{feature.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex gap-3">
                                        <Button
                                            type="button"
                                            variant="primary"
                                            onClick={handleBuyNow}
                                        >
                                            Buy Now
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            onClick={handleBuyNow}
                                        >
                                            Compare Plans
                                        </Button>
                                    </div>
                                </div>

                                {/* Right Column - Demo Booking */}
                                <div className="py-4 pt-0">
                                    <h1 className="text-[40px] font-bold text-primary mb-4 text-center">
                                        Schedule a Live Demo
                                    </h1>
                                    {showThankYou ? (
                                        <div className="text-center py-12">
                                            <div className="mb-6">
                                                <i className="fas fa-check-circle text-6xl text-primary"></i>
                                            </div>
                                            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                                                Thank You!
                                            </h2>
                                            <p className="text-lg text-text-primary">
                                                We appreciate your interest. Our team will contact you shortly.
                                            </p>
                                        </div>
                                    ) : (
                                        <DemoBookingFlow
                                            onComplete={() => setShowThankYou(true)}
                                            onBack={() => window.history.back()}
                                            hideInitialBack={true}
                                            brand={brand}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <Container>
                            {/* Mobile: Logo + by Biziverse */}
                            <div className="flex flex-col items-center mb-12 relative">
                                {brand === 'bz' ? (
                                    <img
                                        src={getImagePath("/Images/Biziverse.png")}
                                        alt="Biziverse"
                                        className="h-[70px] w-auto object-contain mt-5"
                                    />
                                ) : (
                                    <>
                                        <img
                                            src={getImagePath("/Images/QikERP.png")}
                                            alt="QikERP"
                                            className="h-[120px] w-auto object-contain mt-5"
                                        />
                                        <div className="flex flex-col items-center absolute" style={{ top: '110px', right: '5.25rem' }}>
                                            <span className="text-sm text-primary font-bold mb-0.5">by</span>
                                            <img
                                                src={getImagePath("/Images/Biziverse.png")}
                                                alt="Biziverse"
                                                className="h-[26px] w-auto object-contain"
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                            {/* Schedule Demo Heading */}
                            <h2 className="text-[28px] font-bold text-primary text-center mb-4">
                                Schedule a Live Demo
                            </h2>
                            {showThankYou ? (
                                <div className="text-center py-12">
                                    <div className="mb-6">
                                        <i className="fas fa-check-circle text-6xl text-primary"></i>
                                    </div>
                                    <h2 className="text-3xl font-bold text-primary mb-4">Thank You!</h2>
                                    <p className="text-lg text-text-primary">
                                        We appreciate your interest. Our team will contact you shortly.
                                    </p>
                                </div>
                            ) : (
                                <DemoBookingFlow
                                    onComplete={() => setShowThankYou(true)}
                                    onBack={() => window.history.back()}
                                    hideInitialBack={true}
                                    brand={brand}
                                />
                            )}
                        </Container>
                    )}
                </section>
            </main>

            {!isMobile && !showThankYou && <DemoQASection onBuyNow={handleBuyNow} brand={brand} />}

            <Footer className="!bg-background-light" />
        </div>
    );
};

export default BookDemo;